import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { DialogService } from '../../shared/dialog/dialog.service';
import { selectCurrentUser } from '../auth/store/auth.selectors';
import { AuthProfileService } from './auth-profile.service';
import { OrgContextService } from './org-context.service';
import { UserContextActions } from './store/user-context.actions';
import { toUserContext } from './user-context.mapper';
import type {
  OrgSelectionDialogData,
  OrgSelectionModalComponent,
} from './org-selection-modal.component';

/**
 * Ensures an active organization before the authenticated shell renders (FDD §4.2)
 * — you can't land on the dashboard without an org id. Runs on every app load
 * (the org context is in-memory, so it re-resolves after a reload):
 *  - single authorized org  → auto-select it, no prompt;
 *  - multiple orgs          → show the selection modal (pre-selecting the active
 *                             org) and switch to the chosen one.
 * Already-resolved this session → pass straight through. Fails closed to /signin.
 */
export const orgGuard: CanActivateFn = async (): Promise<boolean> => {
  const orgContext = inject(OrgContextService);
  const authProfile = inject(AuthProfileService);
  const dialog = inject(DialogService);
  const router = inject(Router);
  const store = inject(Store);

  if (orgContext.activeOrg()) {
    return true;
  }

  try {
    const profile = await firstValueFrom(authProfile.getProfile());
    orgContext.setProfile(profile);

    if (profile.authorizedOrgs.length === 0) {
      await router.navigate(['/signin'], { replaceUrl: true });
      return false;
    }

    let orgId: number;
    if (profile.authorizedOrgs.length === 1) {
      orgId = profile.authorizedOrgs[0].orgId;
    } else {
      // Lazy-load the modal so its deps stay out of the initial bundle.
      const { OrgSelectionModalComponent: modal } = await import('./org-selection-modal.component');
      const ref = dialog.open<OrgSelectionModalComponent, OrgSelectionDialogData, number>(modal, {
        data: {
          orgs: profile.authorizedOrgs,
          // Pre-select /auth/me `active_org_id`, only when it is one of the listed orgs.
          preselectedOrgId: profile.authorizedOrgs.some((o) => o.orgId === profile.activeOrgId)
            ? profile.activeOrgId
            : null,
        },
        disableClose: true,
        width: '734px',
      });
      const chosen = await firstValueFrom(ref.afterClosed());
      if (chosen == null) {
        await router.navigate(['/signin'], { replaceUrl: true });
        return false;
      }
      orgId = chosen;
    }

    const org = await firstValueFrom(authProfile.switchOrg(orgId));
    orgContext.setActiveOrg(org);

    // Populate the shell's UserContext (nav, guards, org switcher) from the real
    // profile + org context; display name comes from the OIDC identity.
    const user = await firstValueFrom(store.select(selectCurrentUser));
    store.dispatch(
      UserContextActions.loadContextSuccess({
        context: toUserContext(profile, org, user.displayName ?? ''),
      }),
    );
    return true;
  } catch {
    await router.navigate(['/signin'], { replaceUrl: true });
    return false;
  }
};
